import React, { useState, useEffect } from 'react';
import { TextField, MenuItem, Button, InputLabel, styled, Autocomplete, Checkbox, FormControlLabel, Slider, Typography } from '@mui/material';
import Sidebar from '../components/Sidebar';
import config from '../config';

import {useNavigate} from 'react-router-dom';

const ContentContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
});

const FormContainer = styled('form')({
    display: 'flex',
    marginTop: '2rem',
    width: "100%",
    flexDirection: 'column',
    alignItems: 'stretch',
});


const StyledInputLabel = styled(InputLabel)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));


const Header = styled('h1')(({ theme }) => ({
    fontSize: '1.65rem',
    width: '100%',
    fontWeight: 'normal',
    fontFamily: theme.typography.fontFamily,
    color: '#2E2E2E',
    paddingTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  }));


const FormElement = styled('div')({
    margin: "1rem 0"
});

const PageContainer = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    alignContent: 'center',
    width: '100%',
    height: '100%',
  });


const NewRenderPage = () => {
    const [blendFile, setBlendFile] = useState(null);
    const [productTitle, setProductTitle] = useState('');
    const [wristSize, setWristSize] = useState('');
    const [products, setProducts] = useState([]);
    const [wristSizes, setWristSizes] = useState([]);
    const [renderVTO, setRenderVTO] = useState(false);
    const [renderProductImagery, setRenderProductImagery] = useState(false);
    const [renderImageSize, setRenderImageSize] = useState(600);

    const navigate = useNavigate();

    useEffect(() => {
      fetchProducts().then((allProducts) => {
        setProducts(allProducts);
      });
      fetchWristSizes().then((allWristSizes) => {
        setWristSizes(allWristSizes);
      } );
    }
    , []);

    const fetchProducts = async () => {
      const response = await fetch(`${config.serverUrl}/products`);
      const data  = await response.json();
      if (response.status !== 200) {
        alert('Failed to fetch products.');
        return [];
      }
      console.log(data.products);
      return data.products;
    };

    const fetchWristSizes = async () => {
      const response = await fetch(`${config.serverUrl}/wrist-sizes`);
      const data  = await response.json();
      if (response.status !== 200) {
        alert('Failed to fetch wrist sizes.');
        return [];
      }
      console.log(data.wrist_sizes);
      // sort wrist sizes in ascending order
      return data.wrist_sizes.sort((a, b) => a - b);
    };


    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file && file.name.endsWith('.blend')) {
        setBlendFile(file);
      } else {
        alert('Please upload a .blend file.');
      }
    };

  
    const handleSubmit = (e) => {
      e.preventDefault();

      if (!blendFile) {
        alert('Please upload a .blend file.');
        return;
      }

      if (!renderVTO && !renderProductImagery) {
        alert('Please select at least one render type.');
        return;
      }

      const formData = new FormData();
      formData.append('file', blendFile);
      
      startRenderRequest(formData).then((sessionId) => {
        setBlendFile(null);
        setProductTitle('');
        setWristSize('');
        navigate('/review?session_id=' + sessionId);
      });
    };

    const handleProductChange = (event, newValue) => {
      if (newValue) {
        setProductTitle(newValue);
      } else {
        setProductTitle('');
      }
    };


    const startRenderRequest = async (formData) => {
      console.log('Starting render request for product title', productTitle);
      try {
        const response = await fetch(
          `${config.serverUrl}/render?render_vto_flag=${renderVTO}&render_product_imagery_flag=${renderProductImagery}&render_size=${renderImageSize}&wrist_size=${wristSize}&product_title=${productTitle}`,
          {
            method: 'POST',
            body: formData,
          }
        );
    
        if (!response.ok) {
          throw new Error('Failed to start render. status: ' + response.status);
        }
    
        const data = await response.json();
        return data.session_id;
      } catch (error) {
        console.error('Error starting render:', error.message);
        throw error;
      }
    };
  
    return (
      <PageContainer>
        <Sidebar navigate={navigate} />
        <ContentContainer>
          <Header>Start new render</Header>
          <FormContainer onSubmit={handleSubmit}>
              <FormElement>
                <Autocomplete
                  options={products}
                  freeSolo
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Product Title"
                      variant="outlined"
                      value={productTitle}
                      sx={{width: '100%', maxWidth: '500px'}}
                      onChange={(e) => setProductTitle(e.target.value)}
                      required
                    /> 
                  )}
                  value={productTitle}
                  onChange={handleProductChange}
                />
              </FormElement>
              <FormElement>
                <TextField
                    select
                    label="Select Wrist Size"
                    value={wristSize}
                    onChange={(e) => setWristSize(e.target.value)}
                    variant="outlined"
                    sx={{width: '100%', maxWidth: '500px'}}
                    required
                >
                    {wristSizes.map((size) => (
                    <MenuItem key={size} value={size}>
                        {size}
                    </MenuItem>
                    ))}
                </TextField>
              </FormElement>
              <FormElement>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={renderVTO}
                      onChange={(e) => setRenderVTO(e.target.checked)}
                      name="renderVTO"
                      color="primary"
                    />
                  }
                  label="Render VTO"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={renderProductImagery}
                      onChange={(e) => setRenderProductImagery(e.target.checked)}
                      name="renderProductImagery"
                      color="primary"
                    />
                  }
                  label="Render Product Imagery"
                />
              </FormElement>
              <FormElement>
                <Typography id="image-size-slider" gutterBottom>
                    Render Image Size: {renderImageSize}px
                </Typography>
                <Slider
                  value={renderImageSize}
                  onChange={(e, newValue) => setRenderImageSize(newValue)}
                  valueLabelDisplay="auto"
                  aria-labelledby="render-image-size-slider"
                  step={100}
                  marks
                  min={100}
                  max={1000}
                  sx={{width: '100%', maxWidth: '500px'}}
                />
              </FormElement>
              <FormElement>
                  <StyledInputLabel htmlFor="file-upload">Upload Blend File</StyledInputLabel>
                  <input type="file" accept=".blend" onChange={handleFileChange} />
              </FormElement>
              <FormElement>
                <Button variant="contained" color="primary" type="submit">
                    Submit
                </Button>
              </FormElement>
          </FormContainer>
        </ContentContainer>
        </PageContainer>
    );
  };

export default NewRenderPage